import { ContentPanel } from '@/components/content-panel';
import { Text } from '@mantine/core';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { FormikProps } from 'formik';
import { useCallback, useMemo, useRef } from 'react';
import { StandardButton } from '../../components/buttons/standard';
import { PrimaryLayout } from '../../layouts/primary';
import { ResetPasswordForm, ResetPasswordValues } from './form';
import { useResetPasswordMutation } from './graphql.generated';

export function ResetPasswordPage() {
	const navigate = useNavigate();
	const { token } = useSearch({ from: '/auth/reset-password' });
	const [resetPassword, { loading, error }] = useResetPasswordMutation();
	const getMessage = () => {
		if (loading) {
			return 'Resetting password...';
		}

		if (error) {
			return 'An error occurred, please try again later';
		}

		if (!token) {
			return 'This is an invalid reset link';
		}
	};
	const onSubmit = useCallback(
		async (values: ResetPasswordValues) => {
			if (!token) {
				return;
			}

			const { errors } = await resetPassword({
				variables: { token, password: values.password },
			});

			if (!errors?.length) {
				// TODO: Right now passing the redirect_uri through to login means you end up at the NotFound route. I can't work out why right now.
				// Given that it's not needed, i've disabled the pass through for now
				// navigate({ to: '/auth/login', search: { redirect: redirect_uri } });
				navigate({ to: '/auth/login' });
			}
		},
		[navigate, resetPassword, token]
	);

	const formRef = useRef<FormikProps<ResetPasswordValues>>(null);
	const triggerSubmit = useCallback(() => {
		formRef.current?.submitForm();
	}, [formRef]);

	const resetPasswordButton = useMemo(
		() => (
			<StandardButton variant="filled" onClick={triggerSubmit}>
				Reset Password
			</StandardButton>
		),
		[triggerSubmit]
	);

	return (
		<PrimaryLayout
			header={{ title: 'Reset Password', backTo: '/auth/login' }}
			footer={{ children: resetPasswordButton }}
		>
			<ContentPanel gap="md" justify="center" align="center" flex={1}>
				<Text size="sm" fw={400}>
					{getMessage()}
				</Text>
				<ResetPasswordForm onSubmit={onSubmit} formRef={formRef} />
			</ContentPanel>
		</PrimaryLayout>
	);
}
